import _ from "lodash";
import React from "react";
import EMOJIS from "../constants/emojis";
import { FRANCHISE_ROLES } from "../constants/roles";

const moment = require("moment-timezone");

class Util {
  /**
   *
   * @param {number|string} season
   * @param {string} [pathname=window.location.pathname]
   * @returns {*}
   */
  static addSeasonToPath(season, pathname = window.location.pathname) {
    let newPathname;

    const seasonStringPattern = /\/:season(\/|$)/;
    if (seasonStringPattern.test(pathname)) {
      newPathname = pathname.replace(":season", season);
    } else {
      const seasonUrlPattern = /\/([\d]{4})(\/|$)/;
      if (seasonUrlPattern.test(pathname)) {
        const matches = seasonUrlPattern.exec(pathname);
        let end = matches.length >= 2 ? matches[2] : "";
        newPathname = pathname.replace(seasonUrlPattern, `/${season}${end}`);
      } else {
        // don't change the pathname at all
        newPathname = pathname;
      }
    }

    return newPathname;
  }

  /**
   *
   * @param {object} config
   * @returns {{}}
   */
  static addTokenToHeaders(config = {}) {
    const token = localStorage.getItem("token");
    if (token) config.headers = { "x-auth-token": token };
    return config;
  }

  /**
   *
   * @param {number} week
   * @param {string} [pathname=window.location.pathname]
   * @returns {string}
   */
  static addWeekToPath(week, pathname = window.location.pathname) {
    const weekForUrl = Util.weekToUrl(week);

    let newPathname;

    const weekStringPattern = /\/:week(\/|$)/;
    if (weekStringPattern.test(pathname)) {
      newPathname = pathname.replace(":week", weekForUrl);
    } else {
      const weekUrlPattern = /wk-(\d+)/;
      if (weekUrlPattern.test(pathname)) {
        newPathname = pathname.replace(weekUrlPattern, weekForUrl);
      } else {
        // add week to the end of url
        newPathname = `${pathname}/${weekForUrl}`;
      }
    }

    // todo: is this needed?
    // add search and hash at end
    // newPathname += `${window.location.search}${window.location.hash}`;

    return newPathname;
  }

  /**
   *
   * @param object
   * @param value
   * @returns {string}
   */
  static getKeyByValue(object, value) {
    return Object.keys(object).find((key) => object[key] === value);
  }

  /**
   *
   * @param user
   * @param team
   * @returns {boolean}
   */
  static hasDraftManagerRole(user, team) {
    return Util.hasRole(user, team, FRANCHISE_ROLES.DRAFT_MANAGER);
  }

  /**
   *
   * @param user
   * @param team
   * @returns {boolean}
   */
  static hasOfficialRole(user, team) {
    return Util.hasRole(user, team, FRANCHISE_ROLES.OFFICIAL);
  }

  /**
   *
   * @param user
   * @param team
   * @param roleType
   * @returns {boolean}
   */
  static hasRole(user, team, roleType) {
    let hasRole = false;
    if (
      team &&
      team.hasLoaded() &&
      user &&
      team.obj.roles.hasOwnProperty(user.obj.username)
    ) {
      hasRole = team.obj.roles[user.obj.username].value <= roleType;
    }
    return hasRole;
  }

  /**
   *
   * @param {number} time
   * @param {number} [precision=2]
   * @param {boolean} [includeSec=true]
   * @returns {string}
   */
  static formatTimeInSeconds(time, precision = 2, includeSec = true) {
    return (
      _.round(time / 1000, precision).toFixed(precision) +
      (includeSec ? " sec" : "")
    );
  }

  /**
   *
   * @param {string} progress
   * @returns {JSX.Element}
   */
  static getProgressIcon(progress) {
    if (!progress) return null;

    let icon;
    let progressTitle = _.capitalize(_.lowerCase(progress));
    switch (progress) {
      case "done":
        icon = EMOJIS.CHECK_MARK;
        break;
      case "errors":
        icon = EMOJIS.CROSS_MARK;
        break;
      case "doneWithErrors":
        icon = EMOJIS.EXCLAMATION_MARK;
        break;
      case "started":
        icon = EMOJIS.PLAY_BUTTON;
        break;
      case "notStarted":
        icon = EMOJIS.HOURGLASS_NOT_DONE;
        break;
      default:
        throw new Error(
          `Util.getProgressIcon() - ERROR - unknown progress type: ${progress}`
        );
    }

    return <span title={progressTitle}>{icon}</span>;
  }

  /**
   *
   * @param {number} pickNum
   * @param {number} numTeams
   * @returns {number}
   */
  static getRoundFromPickNum(pickNum, numTeams) {
    return Math.floor((pickNum - 1) / numTeams) + 1;
  }

  /**
   *
   * @param {string[]} arr
   * @returns {object[]}
   */
  static makeTableColumns(arr) {
    return arr.map((val) => {
      let value;
      let props = {};

      if (_.isObject(val)) {
        value = val.value;
        delete val.value;
        props = { ...val };
      } else if (_.isString(val)) {
        value = val;
      }

      return {
        Header: _.capitalize(_.lowerCase(value)),
        accessor: _.camelCase(value),
        ...props,
      };
    });
  }

  static toOrdinal(num) {
    if (!_.isNumber(num)) num = parseInt(num, 10);
    if (num > 3 && num < 21) return num + "th";
    switch (num % 10) {
      case 1:
        return num + "st";
      case 2:
        return num + "nd";
      case 3:
        return num + "rd";
      default:
        return num + "th";
    }
  }

  /**
   *
   * @param {number} week
   * @returns {string}
   */
  static weekToUrl(week) {
    const pattern = /^\d+$/;
    if (!pattern.test("" + week)) {
      throw new Error(
        `Util.weekToUrl() - ERROR - week must be an integer: ${week}`
      );
      // week = Util.getCurrentWeek();
    }
    return "wk-" + `0${week}`.substr(-2);
  }

  /**
   *
   * @param {string} weekString
   * @returns {number}
   */
  static weekFromUrl(weekString) {
    const pattern = /wk-(\d+)/;
    if (!pattern.test(weekString))
      throw new Error(
        `Util.weekFromUrl() - ERROR - weekString does not match pattern: ${weekString}`
      );

    const matches = pattern.exec(weekString);
    return parseInt(matches[1], 10);
  }

  /**
   *
   * @param {Date} [currentDate=new Date()]
   * @returns {number}
   */
  static getCurrentSeason(currentDate = new Date()) {
    return parseInt(
      moment(currentDate).subtract(2, "months").format("YYYY"),
      10
    );
  }

  static getWaiversClearDueDate() {
    const waiverProcessDay = 3; // wednesday
    const waiverProcessHour = 18; // 6 pm

    // waiverProcessHour is in easter time
    const timeZone = "America/New_York";

    const now = moment.tz(timeZone);
    const thisWeek = moment
      .tz(timeZone)
      .day(waiverProcessDay)
      .hours(waiverProcessHour)
      .minutes(0)
      .seconds(0)
      .milliseconds(0);
    const nextWeek = moment
      .tz(timeZone)
      .day(waiverProcessDay + 7)
      .hours(waiverProcessHour)
      .minutes(0)
      .seconds(0)
      .milliseconds(0);

    const waiverTime = thisWeek.isBefore(now) ? nextWeek : thisWeek;
    return waiverTime.utc().toDate();
  }

  /**
   *
   * @param {Date} [currentDate=new Date()]
   * @returns {number}
   */
  static getCurrentWeek(currentDate = new Date()) {
    const timeZone = "America/New_York";
    const currentSeason = Util.getCurrentSeason(currentDate);
    console.log(`(Util.getCurrentWeek) - currentSeason: ${currentSeason}`);

    const now = moment(currentDate).tz(timeZone);

    const seasonStartTime = moment.tz(
      `${currentSeason}-03-01 00:00:00`,
      timeZone
    );
    const seasonEndTime = moment(seasonStartTime).add(1, "year");

    if (now.isBefore(seasonStartTime)) {
      throw new Error(
        `Util.getCurrentWeek() - ERROR - currentDate is before start of ${currentSeason} season`
      );
    }

    if (now.isAfter(seasonEndTime))
      throw new Error(
        `Util.getCurrentWeek() - ERROR - currentDate is after end of ${currentSeason} season`
      );

    if (currentSeason !== 2021)
      throw new Error(
        `Util.getCurrentWeek() - ERROR - need to update game start times for new NFL season!!!`
      );

    const lastGameStartTimes = {
      1: "2021-09-13 20:15:00",
      2: "2021-09-20 20:15:00",
      3: "2021-09-27 20:15:00",
      4: "2021-10-04 20:15:00",
      5: "2021-10-11 20:15:00",
      6: "2021-10-18 20:15:00",
      7: "2021-10-25 20:15:00",
      8: "2021-11-01 20:15:00",
      9: "2021-11-08 20:15:00",
      10: "2021-11-15 20:15:00",
      11: "2021-11-22 20:15:00",
      12: "2021-11-29 20:15:00",
      13: "2021-12-06 20:15:00",
      14: "2021-12-13 20:15:00",
      15: "2021-12-20 20:15:00",
      16: "2021-12-27 20:15:00",
      17: "2022-01-03 20:15:00",
    };

    const waiversClearDueDate = Util.getWaiversClearDueDate();

    let currentWeek;
    for (let w = 1; w <= 18; w++) {
      if (moment(waiversClearDueDate).isAfter(lastGameStartTimes[w])) {
        currentWeek = w;
      } else {
        return currentWeek;
      }
    }

    return currentWeek;
  }

  static getWeekPhase(
    currentDate = new Date(),
    haveAnyNflGamesStarted,
    haveAllNflGamesEnded
  ) {
    let phase = "preGames";
    if (haveAnyNflGamesStarted) phase = "duringGames";
    if (haveAllNflGamesEnded) phase = "postGames";

    return phase;
  }

  /**
   *
   * @param {number|string} season
   * @returns {?number}
   */
  static getNumWeeksInNflSeason(season) {
    if (!season) return null;

    // ensure season is an integer
    season = parseInt("" + season, 10);

    if (season < 1978) {
      return 14;
    } else if (season === 1982) {
      // strike-shortened season
      return 9;
    } else if (season < 1990) {
      return 16;
    } else if (season === 1993) {
      // experiment with 2 bye weeks
      return 18;
    } else if (season < 2021) {
      return 17;
    } else {
      return 18;
    }
  }

  /**
   *
   * @param {number} season
   * @returns {boolean}
   */
  static isValidSeason(season) {
    const currentSeason = Util.getCurrentSeason();
    return season >= 2000 && season <= currentSeason;
  }

  /**
   *
   * @param {number} week
   * @param {number} season
   * @returns {boolean}
   */
  static isValidWeek(week, season) {
    const numWeeksInSeason = Util.getNumWeeksInNflSeason(season);
    return week <= numWeeksInSeason;
  }
}

export default Util;
